import { asText } from '@prismicio/helpers';
import { PrismicRichText } from '@prismicio/react';
import { hasRichText } from '@publicss/utils';
import classNames from 'classnames';
import { GatsbyImage } from 'gatsby-plugin-image';
import { gsap } from 'gsap';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import tailwindConfig from '../../../tailwind.config';
import { PageBodyBlock_Six_UpPrimary } from '../../../types/prismic';
// @ts-expect-error - image import
import bgDelivery from '../../assets/images/image--delivery-only.jpg';
import { usePageContext } from '../../providers/PageContext';
import { media } from '../../styles/media';
import { useIsMobile } from '../../utils/breakpoints';
import { formatElementID } from '../../utils/formatElementId';
import { resolveColorCK } from '../../utils/resolveColorCK';
import { Button } from '../ui';

interface BlockSixUpSliceProps {
  slice: {
    primary: PageBodyBlock_Six_UpPrimary;
  };
  sliceIndex: number;
}

const BlockSixUpSlice: React.FunctionComponent<BlockSixUpSliceProps> = ({
  sliceIndex,
  slice: {
    primary: {
      slice_anchor,
      eyebrow,
      heading_start,
      heading_end,
      description,
      button,
      button_link,
      section_heading_one,
      section_icon_one,
      section_description_one,
      section_heading_two,
      section_icon_two,
      section_description_two,
      section_heading_three,
      section_icon_three,
      section_description_three,
      section_heading_four,
      section_icon_four,
      section_description_four,
      section_heading_five,
      section_icon_five,
      section_description_five,
      section_heading_six,
      section_icon_six,
      section_description_six,
      show_numbers,
      background,
      background_side,
      color,
      disclaimer,
    },
  },
}) => {
  const { siteTag } = usePageContext();
  const bgDeliveryRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<any>(null);
  const isMobile = useIsMobile();
  const hasIcons = [
    section_icon_one,
    section_icon_two,
    section_icon_three,
    section_icon_four,
    section_icon_five,
    section_description_six,
  ].some((icon) => icon?.url);

  const isCK = siteTag === 'site:cloudkitchens';

  const sectionIndex = sliceIndex ? sliceIndex : 0;
  const sectionId = slice_anchor?.length
    ? formatElementID(slice_anchor)
    : `block-six-up-${sectionIndex}`;

  useEffect(() => {
    const bgTL = gsap.timeline({
      scrollTrigger: {
        trigger: rootRef.current,
        scrub: 1,
        start: 'top 90%',
      },
    });
    bgTL.fromTo(
      bgDeliveryRef.current,
      { scale: 1.1 },
      { scale: 1, force3D: true },
      0
    );

    const fadeElements = gsap.timeline({
      scrollTrigger: {
        trigger: rootRef.current,
        start: 'top 90%',
      },
    });
    fadeElements.to(rootRef.current, {
      opacity: 1,
      delay: 0.2,
    });
    fadeElements.from(`#${sectionId} .fade-up`, {
      duration: 0.7,
      opacity: '0',
      y: '20%',
      stagger: {
        amount: 0.5,
      },
    });
  }, []);

  const numberClassNames =
    show_numbers === 'yes' ? 'h3 mb-4 color--accent-1' : 'hidden';

  const resolvedColor = resolveColorCK(color);

  const itemDescriptionStyles = classNames(
    'mb-0',
    resolvedColor === 'tertiary' ? 'text-grey-5' : 'text-white-1'
  );

  return (
    <Section
      ref={rootRef}
      id={sectionId}
      className={classNames(
        resolvedColor === 'primary'
          ? 'bg-primary'
          : resolvedColor === 'secondary'
            ? 'bg-secondary'
            : 'bg-transparent'
      )}
      color={resolvedColor as string}
      backgroundSide={background_side as 'left' | 'right'}
      background={background?.url as string}>
      <div className="container pb-16 lg:pb-20">
        <div className="grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12">
          <div
            className={`col-span-8 flex h-full flex-col justify-end py-12
              ${
                description?.richText !== ''
                  ? 'md:col-span-4 lg:col-span-5'
                  : 'md:col-span-7'
              }`}>
            {hasRichText(eyebrow) && (
              <div
                className={classNames(
                  'eyebrow line color--grey-1 fade-up mb-6 sm:mb-12 md:block',
                  {
                    'text-grey-6': siteTag === 'site:profood-solutions',
                  }
                )}>
                {asText(eyebrow.richText)}
              </div>
            )}
            <h2
              className={classNames(
                'heading fade-up sm:pr-8',
                resolvedColor === 'tertiary'
                  ? 'text-secondary'
                  : 'color--grey-1',
                siteTag === 'site:kitchenvalley' ? 'h2-kv' : 'h2'
              )}>
              <span>
                <PrismicRichText field={heading_start?.richText} />
              </span>
              {heading_end?.richText && (
                <>
                  {' '}
                  <span
                    className={
                      resolvedColor === 'tertiary'
                        ? 'text-secondary'
                        : 'color--accent-1'
                    }>
                    <PrismicRichText field={heading_end?.richText} />
                  </span>
                </>
              )}
            </h2>
          </div>
          {hasRichText(description) && (
            <div
              className={classNames(
                'col-span-8 md:col-span-4 lg:col-span-6 lg:col-start-7'
              )}>
              <div
                // className="relative z-10"
                className={classNames(
                  {
                    'bg-primary-dark': resolvedColor === 'primary',
                    'bg-secondary-light': resolvedColor === 'secondary',
                    'bg-grey-1': resolvedColor === 'tertiary',
                  },
                  'bg--right py-12 rtl:after:!bg-[transparent] md:px-16 md:pb-12 md:pt-36'
                )}>
                <div className="relative z-10">
                  <div
                    className={classNames(
                      {
                        'text-grey-1':
                          resolvedColor === 'primary' ||
                          resolvedColor === 'secondary',
                        'text-grey-5': resolvedColor === 'tertiary',
                      },
                      'fade-up color--white md:max-w-xs'
                    )}>
                    <PrismicRichText field={description?.richText} />
                  </div>
                  {hasRichText(button) && (
                    <div className="mr-8 mt-4 md:mr-0">
                      {button_link?.url ? (
                        <Button
                          variant="inverse"
                          href={button_link?.url}
                          fullWidth={isMobile}
                          className="fade-up">
                          {asText(button?.richText)}
                        </Button>
                      ) : (
                        <Button
                          globalTypeform
                          variant="inverse"
                          className="fade-up"
                          fullWidth={isMobile}>
                          {asText(button?.richText)}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div
          className={classNames(
            'grid grid-cols-4 pt-12 md:grid-cols-8 md:pt-24 lg:grid-cols-12',
            resolvedColor === 'tertiary' ? 'text-grey-6' : 'text-white-1'
          )}>
          {hasRichText(section_heading_one) && (
            <div className="fade-up col-span-4 mb-12 pr-6 md:mb-20 lg:col-span-3">
              {hasIcons ? (
                <GatsbyImage
                  className="mb-4 max-w-[32px] md:max-w-[48px]"
                  image={section_icon_one?.gatsbyImageData}
                  alt=""
                />
              ) : (
                <div className={numberClassNames}>01</div>
              )}
              <div className="h4 mb-4">
                <PrismicRichText field={section_heading_one?.richText} />
              </div>
              <p className={itemDescriptionStyles}>
                <PrismicRichText
                  field={section_description_one?.richText}
                  components={{
                    oList: ({ children }) => (
                      <ol className="mb-4 mt-1 list-decimal pl-4">
                        {children}
                      </ol>
                    ),
                    list: ({ children }) => (
                      <ul className="mb-4 mt-1 list-disc pl-4">{children}</ul>
                    ),
                    listItem: ({ children }) => (
                      <li className="mb-2 ml-1 last:mb-0">{children}</li>
                    ),
                    oListItem: ({ children }) => (
                      <li className="mb-2 ml-1 last:mb-0">{children}</li>
                    ),
                  }}
                />
              </p>
            </div>
          )}
          {hasRichText(section_heading_two) && (
            <div className="fade-up col-span-4 mb-12 pr-6 md:mb-20 lg:col-span-3 lg:col-start-5">
              {hasIcons ? (
                <GatsbyImage
                  className="mb-4 max-w-[32px] md:max-w-[48px]"
                  image={section_icon_two?.gatsbyImageData}
                  alt=""
                />
              ) : (
                <div className={numberClassNames}>02</div>
              )}
              <div className="h4 mb-4">
                <PrismicRichText field={section_heading_two?.richText} />
              </div>
              <p className={itemDescriptionStyles}>
                <PrismicRichText
                  field={section_description_two?.richText}
                  components={{
                    oList: ({ children }) => (
                      <ol className="mb-4 mt-1 list-decimal pl-4">
                        {children}
                      </ol>
                    ),
                    list: ({ children }) => (
                      <ul className="mb-4 mt-1 list-disc pl-4">{children}</ul>
                    ),
                    listItem: ({ children }) => (
                      <li className="mb-2 ml-1 last:mb-0">{children}</li>
                    ),
                    oListItem: ({ children }) => (
                      <li className="mb-2 ml-1 last:mb-0">{children}</li>
                    ),
                  }}
                />
              </p>
            </div>
          )}
          {hasRichText(section_heading_three) && (
            <div className="fade-up col-span-4 mb-12 pr-6 md:mb-20 lg:col-span-3 lg:col-start-9">
              {hasIcons ? (
                <GatsbyImage
                  className="mb-4 max-w-[32px] md:max-w-[48px]"
                  image={section_icon_three?.gatsbyImageData}
                  alt=""
                />
              ) : (
                <div className={numberClassNames}>03</div>
              )}
              <div className="h4 mb-4">
                <PrismicRichText field={section_heading_three?.richText} />
              </div>
              <p className={itemDescriptionStyles}>
                <PrismicRichText
                  field={section_description_three?.richText}
                  components={{
                    oList: ({ children }) => (
                      <ol className="mb-4 mt-1 list-decimal pl-4">
                        {children}
                      </ol>
                    ),
                    list: ({ children }) => (
                      <ul className="mb-4 mt-1 list-disc pl-4">{children}</ul>
                    ),
                    listItem: ({ children }) => (
                      <li className="mb-2 ml-1 last:mb-0">{children}</li>
                    ),
                    oListItem: ({ children }) => (
                      <li className="mb-2 ml-1 last:mb-0">{children}</li>
                    ),
                  }}
                />
              </p>
            </div>
          )}
          {hasRichText(section_heading_four) && (
            <div className="fade-up col-span-4 mb-12 pr-6 md:mb-20 lg:col-span-3">
              {hasIcons ? (
                <GatsbyImage
                  className="mb-4 max-w-[32px] md:max-w-[48px]"
                  image={section_icon_four?.gatsbyImageData}
                  alt=""
                />
              ) : (
                <div className={numberClassNames}>04</div>
              )}
              <div className="h4 mb-4">
                <PrismicRichText field={section_heading_four?.richText} />
              </div>
              <p className={itemDescriptionStyles}>
                <PrismicRichText
                  field={section_description_four?.richText}
                  components={{
                    oList: ({ children }) => (
                      <ol className="mb-4 mt-1 list-decimal pl-4">
                        {children}
                      </ol>
                    ),
                    list: ({ children }) => (
                      <ul className="mb-4 mt-1 list-disc pl-4">{children}</ul>
                    ),
                    listItem: ({ children }) => (
                      <li className="mb-2 ml-1 last:mb-0">{children}</li>
                    ),
                    oListItem: ({ children }) => (
                      <li className="mb-2 ml-1 last:mb-0">{children}</li>
                    ),
                  }}
                />{' '}
              </p>
            </div>
          )}
          {hasRichText(section_heading_five) && (
            <div className="fade-up col-span-4 mb-12 pr-6 md:mb-20 lg:col-span-3 lg:col-start-5">
              {hasIcons ? (
                <GatsbyImage
                  className="mb-4 max-w-[32px] md:max-w-[48px]"
                  image={section_icon_five?.gatsbyImageData}
                  alt=""
                />
              ) : (
                <div className={numberClassNames}>05</div>
              )}
              <div className="h4 mb-4">
                <PrismicRichText field={section_heading_five?.richText} />
              </div>
              <p className={itemDescriptionStyles}>
                <PrismicRichText
                  field={section_description_five?.richText}
                  components={{
                    oList: ({ children }) => (
                      <ol className="mb-4 mt-1 list-decimal pl-4">
                        {children}
                      </ol>
                    ),
                    list: ({ children }) => (
                      <ul className="mb-4 mt-1 list-disc pl-4">{children}</ul>
                    ),
                    listItem: ({ children }) => (
                      <li className="mb-2 ml-1 last:mb-0">{children}</li>
                    ),
                    oListItem: ({ children }) => (
                      <li className="mb-2 ml-1 last:mb-0">{children}</li>
                    ),
                  }}
                />
              </p>
            </div>
          )}
          {hasRichText(section_heading_six) && (
            <div className="fade-up col-span-4 mb-6 pr-6 md:mb-20 lg:col-span-3 lg:col-start-9">
              {hasIcons ? (
                <GatsbyImage
                  className="mb-4 max-w-[32px] md:max-w-[48px]"
                  image={section_icon_six?.gatsbyImageData}
                  alt=""
                />
              ) : (
                <div className={numberClassNames}>06</div>
              )}
              <div className="h4 mb-4">
                <PrismicRichText field={section_heading_six?.richText} />
              </div>
              <p className={itemDescriptionStyles}>
                <PrismicRichText
                  field={section_description_six?.richText}
                  components={{
                    oList: ({ children }) => (
                      <ol className="mb-4 mt-1 list-decimal pl-4">
                        {children}
                      </ol>
                    ),
                    list: ({ children }) => (
                      <ul className="mb-4 mt-1 list-disc pl-4">{children}</ul>
                    ),
                    listItem: ({ children }) => (
                      <li className="mb-2 ml-1 last:mb-0">{children}</li>
                    ),
                    oListItem: ({ children }) => (
                      <li className="mb-2 ml-1 last:mb-0">{children}</li>
                    ),
                  }}
                />
              </p>
            </div>
          )}
        </div>
        {isCK && disclaimer && (
          <div className={classNames(itemDescriptionStyles, 'fade-up')}>
            {disclaimer}
          </div>
        )}
      </div>
      {background?.url && (
        <div ref={bgDeliveryRef} className="bg--delivery--holder">
          <div className="bg--delivery" />
        </div>
      )}
    </Section>
  );
};

export default BlockSixUpSlice;

const Section = styled.section<{
  background: string;
  backgroundSide: 'left' | 'right';
  color: string;
}>`
  p a {
    text-decoration: underline;
  }
  overflow: hidden;
  .bg--delivery--holder {
    position: relative;
    width: 100%;
    height: 250px;
    overflow: hidden;
    background: ${({ color }) =>
      color !== 'tertiary'
        ? `linear-gradient(
      180deg,
      ${tailwindConfig.theme.colors[color]} 0,
      ${tailwindConfig.theme.colors[color]} 50%,
      var(--grey-2) 50%,
      var(--grey-2) 100%
    )`
        : null};
    @media ${media.mdUp} {
      padding-inline-start: ${({ backgroundSide }) =>
        backgroundSide === 'right' ? '10%' : ''};
      width: ${({ backgroundSide }) =>
        backgroundSide === 'left' ? '90%' : '100%'};
      height: 670px;
      background: ${({ color }) =>
        color !== 'tertiary'
          ? `linear-gradient(
      180deg,
      ${tailwindConfig.theme.colors[color]} 0,
      ${tailwindConfig.theme.colors[color]} 35%,
      var(--grey-2) 0,
      var(--grey-2) 100%
    )`
          : null};
    }
  }
  .bg--delivery {
    position: absolute;
    z-index: 1;
    width: 90%;
    @media ${media.mdDown} {
      width: 100%;
    }
    height: 100%;
    top: 0;
    ${({ background }) => {
      if (background)
        return `background: var(--grey-2) url(${background}) no-repeat;`;
      return `background: var(--grey-2) url(${bgDelivery}) no-repeat;`;
    }}
    background-position: bottom;
    background-size: cover;
    overflow: hidden;
    @media ${media.mdUp} {
      width: 100%;
      background-position: bottom center;
    }
  }
  .heading {
    h2 {
      display: inline;
    }
  }
  .bg--right {
    position: relative;
    @media ${media.mdUp} {
      &::after {
        position: absolute;
        z-index: 1;
        content: '';
        bottom: 0;
        inset-inline-start: -100%;
        height: 100%;
        width: 200%;
        background: ${({ color }) =>
          color !== 'tertiary' ? tailwindConfig.theme.colors[color] : null};
        @media ${media.mdUp} {
          inset-inline-end: -99%;
          inset-inline-start: auto;
          width: 100%;
        }
      }
    }
    @media ${media.mdDown} {
      &::before {
        background-color: inherit;
        height: 100%;
        width: 120vw;
        z-index: 1;
        content: '';
        position: absolute;
        top: 0;
        inset-inline-start: -20%;
      }
    }
  }
`;
